import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useCallback,
  useMemo,
  memo,
  type FormEvent,
  type PropsWithChildren,
} from "react";
import clsx from "clsx";
import type { FieldErrors } from "react-hook-form";

import { notify } from "../notify";

import type { IFormLayoutProps } from ".";

import { useUiStore } from "@/stores/uiStore";
import { Container } from "@components/layout/container";
import { FormButtons } from "@components/organisms/formButtons";
import { FastButton } from "@components/fastFields/fastButton";
import { useTranslateErrorObj } from "@/hooks/useTranslateErrorObj";
import { extractReactFormErrorMessages } from "@/utils";

export const FormLayout = memo(function FormLayout<TSchema extends object>(
  props: PropsWithChildren<IFormLayoutProps<TSchema>>,
) {
  const {
    children,
    fileIsLoading,
    forceMode,
    onCancel,
    onNotifySubmit,
    onSubmit,
    isLoading,
    className,
    noBack = false,
    noButtons,
    noMarginTop,
    ...formButtonsProps
  } = props;
  const storeMode = useUiStore((state) => state.pageMode);
  const mode = useMemo(() => forceMode || storeMode, [forceMode, storeMode]);
  const { t } = useTranslation("error");
  const navigate = useNavigate();

  const { notifyErrors } = useTranslateErrorObj();

  const handleCancel = useCallback(() => {
    if (onCancel && typeof onCancel === "function") {
      onCancel();
    } else navigate(-1);
  }, [onCancel, navigate]);

  const errorMessagesContent = useCallback(
    (errors: FieldErrors<TSchema>) => extractReactFormErrorMessages(errors),
    [],
  );

  const submitHandler = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (fileIsLoading) {
        notify.warning({ title: t("validation.files.isUploading") });
      } else {
        onNotifySubmit?.((errors, name) => {
          if (name === undefined) notifyErrors(errorMessagesContent(errors));
          else if (typeof name === "string")
            notifyErrors(errorMessagesContent(errors), name);
          else notifyErrors(errorMessagesContent(errors), name);
        })(event);

        onSubmit?.(event);
      }
    },
    [
      fileIsLoading,
      t,
      onNotifySubmit,
      onSubmit,
      errorMessagesContent,
      notifyErrors,
    ],
  );

  const getFormComponent = useCallback(() => {
    return (
      <form onSubmit={submitHandler} className={clsx("w-100", className)}>
        <Container className={clsx(!noMarginTop && "mt-24")}>
          {children}
          {!noButtons && (
            <FormButtons
              isLoading={isLoading}
              cancelButtonProps={{ onClick: handleCancel }}
              noBack={noBack}
              {...formButtonsProps}
            />
          )}
        </Container>
      </form>
    );
  }, [
    submitHandler,
    handleCancel,
    className,
    noMarginTop,
    noButtons,
    isLoading,
    noBack,
    formButtonsProps,
    children,
  ]);

  const handleClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const getContainerComponent = useCallback(() => {
    return (
      <Container className={clsx(!noMarginTop && "mt-24")}>
        {!noBack && (
          <div className={"w-100"}>
            <FastButton type={"back"} onClick={handleClick} />
          </div>
        )}
        {children}
      </Container>
    );
  }, [children, noMarginTop, noBack, handleClick]);

  return mode === "create" ? getFormComponent() : getContainerComponent();
});
FormLayout.displayName = "FormLayout";
